import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  userApi,
  organisationApi,
  getMyEffectiveOrganisationPermissions,
} from '../../services/api';
import { hasBookingsHubPermission } from '../bookings-admin/bookingsHubPermissions';
import NotificationBell from './NotificationBell';
import NetworkAlertBanner from './NetworkAlertBanner';
import TwoSvBypassBanner from './TwoSvBypassBanner';
import UpdateBanner from './UpdateBanner';
import TxHistoryProvider from '../TxHistoryProvider';
import './Layout.css';

const responsePayload = (response) => response?.data?.data || response?.data || {};
const SIDEBAR_COLLAPSED_KEY = 'studioaeye_sidebar_collapsed';

export const readSidebarCollapsed = () => {
  try { return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
};

export const writeSidebarCollapsed = (collapsed) => {
  try { window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed)); } catch { /* Preference storage is optional. */ }
};

export const hasChoreographyView = (capabilityResponse) => {
  const value = responsePayload(capabilityResponse);
  if (value?.applications?.choreo) return value.applications.choreo.allowed === true;
  const permissions = value?.permissions || value?.capabilities || value;
  return permissions?.choreography_view === true;
};

export const withChoreographyNavigation = (items, canViewChoreography) => {
  const withoutChoreography = items.filter((item) => item.path !== '/app/choreography');
  if (!canViewChoreography) return withoutChoreography;
  const choreography = { path: '/app/choreography', label: 'Choreography', icon: '♫' };
  const profileIndex = withoutChoreography.findIndex((item) => item.path === '/app/profile');
  return profileIndex === -1 ? [...withoutChoreography, choreography] : [...withoutChoreography.slice(0, profileIndex), choreography, ...withoutChoreography.slice(profileIndex)];
};

export const hasLedgerView = (capabilityResponse) => {
  const applications = responsePayload(capabilityResponse)?.applications || {};
  return applications.ledger?.allowed === true || applications.bookkeeping?.allowed === true;
};

// M015-F0032 (WebUI #52): the teacher Ledger page performs ledger.read reads,
// so it is gated strictly on the ledger application (bookkeeping.allowed alone
// does not grant it).
export const hasLedgerPageView = (capabilityResponse) => {
  return responsePayload(capabilityResponse)?.applications?.ledger?.allowed === true;
};

export const withLedgerNavigation = (items, canViewLedger, ledgerEntry = null) => {
  const withoutLedger = items.filter((item) => !item.path.startsWith('/app/ledger'));
  if (!canViewLedger && !ledgerEntry) return withoutLedger;
  const ledger = ledgerEntry || { path: '/app/ledger', label: 'Ledger', icon: '📒' };
  const profileIndex = withoutLedger.findIndex((item) => item.path === '/app/profile');
  return profileIndex === -1 ? [...withoutLedger, ledger] : [...withoutLedger.slice(0, profileIndex), ledger, ...withoutLedger.slice(profileIndex)];
};

// M005-F0052 (M005-D005 §4.3): the Bookings hub's nav entry appears only for
// an actor holding at least one of bookings_manage_all / pricing_plans_view /
// pricing_booking_reprice in ANY organisation they belong to (the caller
// aggregates the per-org my-permissions reads; hasBookingsHubPermission
// decides per org). An actor with none of these keys keeps /app/event-simple
// as their landing page and never sees the hub link — nothing reachable
// today becomes unreachable, nothing new is thrust on someone it isn't for
// (WEB-BKHUB-002).
export const withBookingsHubNavigation = (items, canViewBookingsHub) => {
  // An empty nav list means this user gets NO navigation at all (the
  // event_simple / simple_room_controls single-page layouts) — never inject
  // hub chrome into that shape.
  if (!Array.isArray(items) || items.length === 0) return items;
  const withoutHub = items.filter((item) => item.path !== '/app/bookings');
  if (!canViewBookingsHub) return withoutHub;
  const hub = { path: '/app/bookings', label: 'Bookings Hub', icon: '🗓️' };
  const profileIndex = withoutHub.findIndex((item) => item.path === '/app/profile');
  return profileIndex === -1 ? [...withoutHub, hub] : [...withoutHub.slice(0, profileIndex), hub, ...withoutHub.slice(profileIndex)];
};

const LayoutShell = ({ children }) => {
  const { user, logout, isAdmin, isTeacher } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(readSidebarCollapsed);
  const [darkMode, setDarkMode] = useState(false);
  const [canViewChoreography, setCanViewChoreography] = useState(false);
  const [canViewLedger, setCanViewLedger] = useState(false);
  const [canViewLedgerPage, setCanViewLedgerPage] = useState(false);
  const [canViewBookingsHub, setCanViewBookingsHub] = useState(false);

  useEffect(() => {
    let current = true;
    setCanViewChoreography(false);
    setCanViewLedger(false);
    setCanViewLedgerPage(false);
    if (!user?.id) return () => { current = false; };

    const loadCapability = async () => {
      try {
        const response = await userApi.getApplicationAccess();
        if (current) {
          setCanViewChoreography(hasChoreographyView(response));
          setCanViewLedger(hasLedgerView(response));
          setCanViewLedgerPage(hasLedgerPageView(response));
        }
      } catch {
        if (current) {
          setCanViewChoreography(false);
          setCanViewLedger(false);
          setCanViewLedgerPage(false);
        }
      }
    };
    loadCapability();
    return () => { current = false; };
  }, [user?.id]);

  // M005-F0052 (§4.3): the hub nav gate is permission-derived per
  // organisation via the canonical my-permissions read, never a global flag
  // or role name. Any org granting one of the three keys flips the entry on;
  // every read failure fails closed ({} → no entry) — WEB-BKHUB-002.
  useEffect(() => {
    let current = true;
    setCanViewBookingsHub(false);
    if (!user?.id) return () => { current = false; };

    const loadBookingsHubAccess = async () => {
      try {
        const response = await organisationApi.getMine();
        const orgs = response?.data?.data ?? response?.data ?? [];
        const list = Array.isArray(orgs) ? orgs : [];
        for (const org of list) {
          const orgId = org?.id ?? org?.organisation_id;
          if (orgId === undefined || orgId === null) continue;
          // eslint-disable-next-line no-await-in-loop
          const permissions = await getMyEffectiveOrganisationPermissions(orgId);
          if (hasBookingsHubPermission(permissions)) {
            if (current) setCanViewBookingsHub(true);
            return;
          }
        }
      } catch {
        // Fail closed: no permissions readable → no hub entry.
      }
    };
    loadBookingsHubAccess();
    return () => { current = false; };
  }, [user?.id]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    document.documentElement.setAttribute('data-theme', darkMode ? 'light' : 'dark');
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((current) => {
      const next = !current;
      writeSidebarCollapsed(next);
      return next;
    });
  };

  const getNavItems = () => {
    // Event Simple users get NO navigation - only one page
    if (user?.role === 'event_simple') {
      return []; // Empty array - no navigation items
    }
    // Simple Room Controls users get NO navigation - single page experience
    if (user?.role === 'simple_room_controls') {
      return []; // Empty array - no navigation items
    }
    // Super Admin gets full access to all admin features
    if (user?.role === 'super_admin') {
      return [
        { path: '/app/event-simple', label: 'Bookings', icon: '🏠' }, // Default home page
        { path: '/app/admin/organisations', label: 'Organisations', icon: '🏢' },
        { path: '/app/admin/users', label: 'Users', icon: '👥' },
        { path: '/app/admin/rooms', label: 'Rooms', icon: '🚪' },
        { path: '/app/admin/consoles', label: 'Panels', icon: '📋' },
        { path: '/app/teacher/classes', label: 'Classes', icon: '📚' },
        { path: '/app/book-room', label: 'Book Room', icon: '📅' },
        { path: '/app/admin/devices', label: 'Devices', icon: '📹' },
        { path: '/app/admin/media', label: 'Media', icon: '🎬' },
        { path: '/app/admin/payments', label: 'Payments', icon: '💳' },
        { path: '/app/ledger/bookkeeping', label: 'Booking Keeper', icon: '🧾' },
        { path: '/app/admin/settings', label: 'Settings', icon: '⚙️' },
        { path: '/app/panoptic', label: 'PanOptic', icon: '◉' },
        { path: '/app/admin/security', label: 'Security', icon: '🔐' },
        { path: '/app/admin/network', label: 'Network', icon: '🛜' },
        { path: '/app/admin/realm-authority/groups', label: 'Access Authority', icon: '🔑' },
        { path: '/app/profile', label: 'Profile', icon: '👤' },
      ];
    }
    // Admin gets limited access: Bookings, Users, Rooms, Profile
    if (isAdmin()) {
      return [
        { path: '/app/event-simple', label: 'Bookings', icon: '🏠' }, // Default home page
        // M004-F0654 (WebUI #124): the organisations the admin administers.
        { path: '/app/admin/organisations', label: 'Organisations', icon: '🏢' },
        { path: '/app/admin/users', label: 'Users', icon: '👥' },
        { path: '/app/admin/rooms', label: 'Rooms', icon: '🚪' },
        { path: '/app/teacher/classes', label: 'Classes', icon: '📚' },
        { path: '/app/ledger/bookkeeping', label: 'Booking Keeper', icon: '🧾' },
        { path: '/app/panoptic', label: 'PanOptic', icon: '◉' },
        { path: '/app/admin/security', label: 'Security', icon: '🔐' },
        { path: '/app/admin/network', label: 'Network', icon: '🛜' },
        { path: '/app/admin/realm-authority/groups', label: 'Access Authority', icon: '🔑' },
        { path: '/app/profile', label: 'Profile', icon: '👤' },
      ];
    }
    if (isTeacher()) {
      return [
        { path: '/app/event-simple', label: 'Bookings', icon: '🏠' }, // Default home page
        { path: '/app/teacher/classes', label: 'Classes', icon: '📚' },
        { path: '/app/profile', label: 'Profile', icon: '👤' },
      ];
    }
    if (['bookkeeper', 'organisation_admin'].includes(String(user?.role || '').toLowerCase())) {
      return [
        { path: '/app/ledger/bookkeeping', label: 'Booking Keeper', icon: '🧾' },
        { path: '/app/profile', label: 'Profile', icon: '👤' },
      ];
    }
    // Student/Parent
    return [
      { path: '/app/profile', label: 'Profile', icon: '👤' },
    ];
  };

  // M015-F0032 (WebUI #52): teachers with the ledger application get their
  // Ledger page in the teacher view — still exactly one Ledger link (T-LED-NODEUI-004).
  const teacherLedgerEntry = isTeacher() && canViewLedgerPage
    ? { path: '/app/teacher/ledger', label: 'Ledger', icon: '📒' }
    : null;
  const navItems = withChoreographyNavigation(
    withLedgerNavigation(
      withBookingsHubNavigation(getNavItems(), canViewBookingsHub),
      canViewLedger,
      teacherLedgerEntry,
    ),
    canViewChoreography,
  );
  const isEventSimple = user?.role === 'event_simple';
  const isSimpleRoomControls = user?.role === 'simple_room_controls';

  // Simple Room Controls users get a minimal layout with NO navigation
  if (isSimpleRoomControls) {
    return (
      <div className="layout event-simple-layout">
        <UpdateBanner />
        <main className="main-content event-simple-main">
          {children}
        </main>
      </div>
    );
  }

  // Event Simple users get a simplified layout with NO navigation
  if (isEventSimple) {
    return (
      <div className="layout event-simple-layout">
        {/* Update required banner — pinned to top */}
        <UpdateBanner />

        {/* Simple header for event_simple - no navigation */}
        <header className="event-simple-header">
          <h1 className="logo">StudioAEye</h1>
          <div className="header-actions">
            <span className="user-name">{user?.displayName || user?.email}</span>
            <button className="btn btn-outline logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        {/* Main content - no sidebar */}
        <main className="main-content event-simple-main">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className={`layout ${sidebarOpen ? 'sidebar-open' : ''} ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Update required banner — pinned to top */}
      <UpdateBanner />

      {/* Mobile header */}
      <header className="mobile-header">
        <button className="menu-toggle" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle menu">
          <span></span>
          <span></span>
          <span></span>
        </button>
        <h1 className="logo">StudioAEye</h1>
        <div className="header-actions">
          <NotificationBell />
          <button className="theme-toggle" onClick={toggleTheme} aria-label="Toggle theme">
            {darkMode ? '☀️' : '🌙'}
          </button>
        </div>
      </header>
      <TwoSvBypassBanner placement="mobile" />

      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="logo"><span className="sidebar-brand-name">StudioAEye</span><span className="sidebar-brand-icon" aria-hidden="true">◉</span></h1>
          <button className="close-sidebar" onClick={() => setSidebarOpen(false)} aria-label="Close menu">
            ✕
          </button>
        </div>

        <nav className="sidebar-nav">
          <TwoSvBypassBanner />
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              aria-label={item.label}
              title={sidebarCollapsed ? item.label : undefined}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.displayName?.[0] || user?.email?.[0] || '?'}</div>
            <div className="user-details">
              <span className="user-name">{user?.displayName || 'User'}</span>
              <span className="user-role">{user?.role || 'Student'}</span>
            </div>
          </div>
          <div className="sidebar-actions">
            <NotificationBell />
            <button className="theme-btn" onClick={toggleTheme} aria-label="Toggle theme">
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button className="logout-btn" onClick={handleLogout} aria-label="Logout" title={sidebarCollapsed ? 'Logout' : undefined}>
              <span className="sidebar-action-icon" aria-hidden="true">↪</span><span className="sidebar-action-label">Logout</span>
            </button>
          </div>
          <button className="sidebar-collapse-btn" type="button" onClick={toggleSidebar} aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'} aria-expanded={!sidebarCollapsed} title={sidebarCollapsed ? 'Expand menu' : undefined}>
            <span aria-hidden="true">{sidebarCollapsed ? '»' : '«'}</span><span className="sidebar-collapse-label">Collapse menu</span>
          </button>
        </div>
      </aside>

      {/* Overlay for mobile */}
      <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>

      {/* Main content */}
      <main className="main-content">
        <NetworkAlertBanner />
        {children}
      </main>

      {/* Mobile bottom nav */}
      <nav className="bottom-nav">
        {navItems.slice(0, 5).map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`bottom-nav-item ${location.pathname === item.path || location.pathname.startsWith(`${item.path}/`) ? 'active' : ''}`}
          >
            <span className="nav-icon">{item.icon}</span>
            <span className="nav-label">{item.label}</span>
          </Link>
        ))}
      </nav>
    </div>
  );
};

// M004-F0712: the ONE history drawer lives here so every chip on every authenticated page can open it.
const Layout = (props) => <TxHistoryProvider><LayoutShell {...props} /></TxHistoryProvider>;

export default Layout;
